import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import { ChangeEvent, useState } from 'react';
import { Answer, Question, QuestionAnswers, QuestionType } from 'src/types/quiz';
import css from './modal.module.scss';
import QuizPageIndicator from './quiz-page-indicator';
import SingleAnswers from './single-answers';
import MultiAnswer from './multi-answer';
import QuizButtons from './modal-buttons';

interface QuizBlockProps {
  questions: Question[];
  selectedAnswers: QuestionAnswers[];
  setSelectedAnswers: React.Dispatch<React.SetStateAction<QuestionAnswers[]>>;
  applyQuiz: () => void;
  correctAnswers: string[];
}

export default function QuizBlock({
  questions,
  selectedAnswers,
  setSelectedAnswers,
  applyQuiz,
  correctAnswers
}: Readonly<QuizBlockProps>) {
  const [index, setIndex] = useState(0);

  const current = questions?.[index];

  const move = (i: number) => setIndex(i);

  const nextBtnAction = () => (index !== questions.length - 1 ? move(index + 1) : applyQuiz());

  const prevBtnAction = () => index !== 0 && move(index - 1);

  const setAnswer = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    const newAnswers = selectedAnswers.filter((q) => q.questionId !== current.id);
    setSelectedAnswers([...newAnswers, { questionId: current.id, answerIds: [value] }]);
  };

  const setMultiAnswer = (value: string) => {
    const currentQuestion = selectedAnswers.filter((q) => q.questionId === current.id)[0] ?? {
      questionId: current.id,
      answerIds: []
    };
    const filtered = selectedAnswers.filter((q) => q.questionId !== current.id);
    if (currentQuestion?.answerIds.includes(value)) {
      currentQuestion.answerIds = currentQuestion.answerIds.filter((a: string) => a !== value);
    } else {
      currentQuestion.answerIds.push(value);
    }
    currentQuestion.answerIds.length > 0 && filtered.push(currentQuestion);
    value.length > 0 && setSelectedAnswers(filtered);
  };

  const isSelected = (key: string) => selectedAnswers.filter((i) => i.answerIds.includes(key)).length > 0;
  const hasAnswer = (i: number) => selectedAnswers.some((q) => q.questionId === questions[i].id);

  const getClassName = (i: number) => (i === index ? css.current : previousClass(i));

  const previousClass = (i: number) => (hasAnswer(i) ? css.previous : '');

  const correctClass = (id: string) => correctAnswers.includes(id);

  return (
    <>
      <div className={css.quiz_text}>
        <QuizPageIndicator onClick={move} quantity={questions.length} getClassName={getClassName} />
        <p>{current?.questionText} </p>
      </div>
      {current && current.type !== QuestionType.MultipleAnswer && (
        <FormControl>
          <RadioGroup
            defaultValue={''}
            className={css.answers_form}
            onChange={setAnswer}
            aria-labelledby='quiz'
            name={current.id}>
            {current.answers?.map((item: Answer) => {
              return (
                <SingleAnswers
                  key={item.id}
                  name={item.id}
                  label={item.answerText}
                  isSelected={isSelected(item.id)}
                  type={current.type}
                  correctClass={correctClass(item.id)}
                />
              );
            })}
          </RadioGroup>
        </FormControl>
      )}
      {current && current.type === QuestionType.MultipleAnswer && (
        <div className={css.multi}>
          {current.answers?.map((item: Answer) => {
            return (
              <MultiAnswer
                key={item.id}
                name={item.id}
                label={item.answerText}
                isSelected={isSelected(item.id)}
                onSelect={setMultiAnswer}
                correctClass={correctClass(item.id)}
              />
            );
          })}
        </div>
      )}
      <QuizButtons
        cancelMsg='Попереднє'
        applyMsg={index !== questions.length - 1 ? 'Наступне' : 'Завершити'}
        onApply={nextBtnAction}
        onCancel={prevBtnAction}
        applyBtnClass={css.cancel}
      />
    </>
  );
}
